import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import useApiClient from '../../../services';
import { RootState } from '../../../state/features/reducers';
import { use } from 'i18next';
/**
 * Embeds a Grafana dashboard into a page.
 * See `defaultProps` for other parameters. */

type GrafanaDashboardProps = {
    src: string;
    title?: string;
    kioskMode?: string;
    height?: string;
    width?: string;
    style?: React.CSSProperties;
    className?: string;
    refreshKey?: any;
    isDarkMode?: boolean;
};

export const GrafanaDashboard = ({
    src,
    title,
    kioskMode = '1',
    height = '100%',
    width = '100%',
    style = {},
    className,
    refreshKey,
    isDarkMode = true,
}: GrafanaDashboardProps) => {
    const token = useSelector(
        (x: RootState) => x?.auth?.user?.accessToken,
    );
    // Set the theme based on the dark mode setting-
    const theme = isDarkMode ? 'dark' : 'light';

    const api = useApiClient();
    const [url, setURL] = useState('');
    useEffect(() => {
        // This is a bit of hack to prevent the user from using grafana keyboard shortcuts in the embedded view
        // it would be preferable to attach intercepting keypress event listeners but that required grafana on the same domain
        // solution from: https://github.com/grafana/grafana/issues/13706#issuecomment-1263109009
        const listener = window.addEventListener('blur', (e) => {
            setTimeout(function () {
                window.focus();
            }, 0);
        });
        const refreshInterval = 45 * 60 * 1000; // our auth tokens expire after 1 hour, so refresh every 45 minutes
        const refreshIntervalFunc = setInterval(async () => {
            if (token) {
                const isExpired = api?.checkSessionExpiry(token);
                if (isExpired) {
                    await api?.refreshToken();
                }
            }
        }, refreshInterval);
        return () => {
            if (listener != null) {
                window.removeEventListener('blur', listener);
                clearInterval(refreshIntervalFunc);
            }
        };
    }, []);

    useEffect(() => {
        const origin = api?.getOrigin();
        if (!origin) return;
        let tmpUrl = src;

        // Strip old proxy URL format stored in the database pre-LT-2911.
        // Old: https://<host>/grafana/customer-portal/api/v1-proxy/<path>
        // New: https://<host>/customer-portal/api/v1/grafana-proxy/<path>
        // The new path contains /v1/grafana-proxy, not /v1-proxy, so this check is safe.
        const OLD_PROXY_BASE = '/customer-portal/api/v1-proxy';
        const oldProxyIdx = tmpUrl?.indexOf(OLD_PROXY_BASE);
        if (oldProxyIdx !== undefined && oldProxyIdx >= 0) {
            tmpUrl = tmpUrl.slice(oldProxyIdx + OLD_PROXY_BASE.length);
        }

        const isHostInUrl = tmpUrl?.includes(origin);
        if (!isHostInUrl) {
            const proxyPath = '/customer-portal/api/v1/grafana-proxy';
            if (!tmpUrl?.startsWith(proxyPath)) {
                tmpUrl = proxyPath + tmpUrl;
            }
            tmpUrl = origin + tmpUrl;
        }
        const separator = tmpUrl.includes('?') ? '&' : '?';
        tmpUrl += `${separator}theme=${theme}&kiosk=${kioskMode}`;
        setURL(tmpUrl);
    }, [src, isDarkMode]);

    return (
        <iframe
            key={refreshKey}
            title={title}
            id={title}
            src={url}
            width={width}
            height={height}
            style={{ border: 'none', ...style }}
            className={className}
            sandbox="allow-scripts allow-same-origin"
        />
    );
};
